import type { User } from "@/common/types/user";
import type { SimplePermissionName } from "@/common/types/permission";
import type { Ref } from "vue";
import { computed, type ComputedRef, ref } from "vue";
import Config from "@/common/config";
import { useAuthStore } from "@/stores/auth";

const permissions: Ref<Array<string>> = ref([]);
const permissionsReady = ref(false);

const updatePermissions = (user: User): void => {
  permissionsReady.value = false;
  permissions.value = user.permissions.map(
    (permission) => permission.permissionName,
  );
  permissionsReady.value = true;
};

const clearPermissions = (): void => {
  permissionsReady.value = false;
  permissions.value = [];
};

const permissionPresent = (permission: SimplePermissionName): boolean => {
  return permissions.value.includes(permission);
};

const hasItrRights: ComputedRef<boolean> = computed(() =>
  permissionPresent("REPORT_ITR"),
);

const isVIP: ComputedRef<boolean> = computed(
  () => permissionPresent("ALL_DEPARTMENTS_FOR_DASHBOARD") && Config.isCompass,
);

const as400PermissionPresent = (permission: string): ComputedRef<boolean> =>
  computed(() => {
    const authStore = useAuthStore();
    const perms = authStore.currentUser?.compassInformation?.permissions;
    return Array.isArray(perms) && perms.includes(permission);
  });

const hasPermissionUsageQueryAccess: ComputedRef<boolean> = computed(() => {
  const authStore = useAuthStore();
  const compassInformation = authStore.currentUser?.compassInformation;
  const hasManageUsersPermission =
    Array.isArray(compassInformation?.permissions) &&
    compassInformation.permissions.includes("MANAGE_USERS");
  const hasManagedPermissions =
    Array.isArray(compassInformation?.managedPermissionIds) &&
    compassInformation.managedPermissionIds.length > 0;

  return hasManageUsersPermission || hasManagedPermissions;
});

export const usePermissions = (): {
  updatePermissions: typeof updatePermissions;
  clearPermissions: typeof clearPermissions;
  permissionsReady: typeof permissionsReady;
  permissionPresent: typeof permissionPresent;
  isVIP: typeof isVIP;
  hasItrRights: typeof hasItrRights;
  as400PermissionPresent: typeof as400PermissionPresent;
  hasPermissionUsageQueryAccess: typeof hasPermissionUsageQueryAccess;
} => {
  return {
    updatePermissions,
    clearPermissions,
    permissionsReady,
    permissionPresent,
    isVIP,
    hasItrRights,
    as400PermissionPresent,
    hasPermissionUsageQueryAccess,
  };
};
